import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  ConfigProvider,
  Divider,
  Flex,
  Input,
  Modal,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from '@duxton-web/ui';
import { ColumnsType } from '@duxton-web/ui/es/table';

import { ObjectDetailPage } from '@/common/components';
import { VariableState } from '@/constants';
import BulkDeletionTab from '@/features/admin/bulk-deletion-tab';
import {
  FetchStatus,
  OperationStatus,
  VariableRow,
} from '@/features/admin/types';
import ServiceOnboardingTab from '@/features/service-onboarding';
import VariableComplexityTab from '@/features/variable-complexity';
import VariableComplexityPolicyTab from '@/features/variable-complexity-policy';
import AppService, { VariableInfo } from '@/lib/App';
import { extractErrorMessage } from '@/lib/utils/error';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ArchivalItem {
  name: string;
  status: OperationStatus;
  error?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_TAG: Record<OperationStatus, React.ReactNode> = {
  pending: (
    <Tag color="processing" icon={<Spin size="small" />}>
      In Progress
    </Tag>
  ),
  success: <Tag color="success">Done</Tag>,
  error: <Tag color="error">Failed</Tag>,
};

// ---------------------------------------------------------------------------
// BulkArchivalTab
// ---------------------------------------------------------------------------

const BulkArchivalTab: React.FC = () => {
  const [inputValue, setInputValue] = useState('');

  // Step 1: fetching variable info
  const [fetchStatus, setFetchStatus] = useState<FetchStatus>('idle');
  const [variableRows, setVariableRows] = useState<VariableRow[]>([]);

  // Selection: tracked by name so it survives search filtering
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  // Search within the preview table
  const [searchText, setSearchText] = useState('');

  // Confirmation modal
  const [modalOpen, setModalOpen] = useState(false);

  // Step 2: archival results
  const [archivalItems, setArchivalItems] = useState<ArchivalItem[]>([]);
  const [isArchiving, setIsArchiving] = useState(false);

  // -------------------------------------------------------------------------
  // Parse names from textarea
  // -------------------------------------------------------------------------
  const parsedNames = useMemo(
    () => [
      ...new Set(
        inputValue
          .split(/[\n,]+/)
          .map((s) => s.trim())
          .filter(Boolean),
      ),
    ],
    [inputValue],
  );

  // -------------------------------------------------------------------------
  // Archival execution
  // -------------------------------------------------------------------------
  const updateArchivalItem = useCallback(
    (name: string, status: OperationStatus, error?: string) => {
      setArchivalItems((prev) =>
        prev.map((item) =>
          item.name === name ? { ...item, status, error } : item,
        ),
      );
    },
    [],
  );

  const runArchival = useCallback(
    async (namesToArchive: string[]) => {
      if (namesToArchive.length === 0) return;

      const initialItems: ArchivalItem[] = namesToArchive.map((name) => ({
        name,
        status: 'pending',
      }));
      setArchivalItems(initialItems);
      setIsArchiving(true);

      await Promise.allSettled(
        namesToArchive.map((name) =>
          AppService.planner
            .archiveVariable(name)
            .then(() => updateArchivalItem(name, 'success'))
            .catch((err: unknown) =>
              updateArchivalItem(name, 'error', extractErrorMessage(err)),
            ),
        ),
      );

      setIsArchiving(false);
    },
    [updateArchivalItem],
  );

  const handleProceedArchival = useCallback(() => {
    runArchival([...selectedKeys]);
  }, [runArchival, selectedKeys]);

  // -------------------------------------------------------------------------
  // Step 1: Fetch variable info for each name
  // -------------------------------------------------------------------------
  const handleFetchVariables = useCallback(async () => {
    if (parsedNames.length === 0) return;

    setFetchStatus('loading');
    setVariableRows([]);
    setSelectedKeys(new Set());
    setArchivalItems([]);
    setSearchText('');

    const results = await Promise.allSettled(
      parsedNames.map((name) => AppService.planner.fetchVariable(name)),
    );

    const rows: VariableRow[] = results.map((result, idx) => {
      const name = parsedNames[idx];
      if (result.status === 'fulfilled') {
        const info = result.value as VariableInfo;
        return {
          name,
          state: info?.state ?? 'unknown',
          lastTriggeredAt: info?.xmsUpdatedAt,
        };
      }
      return { name, state: 'unknown' };
    });

    setVariableRows(rows);
    // Pre-select only non-active variables; active ones should be unchecked by default
    setSelectedKeys(
      new Set(
        rows.filter((r) => r.state !== VariableState.ACTIVE).map((r) => r.name),
      ),
    );

    const hasActive = rows.some((r) => r.state === VariableState.ACTIVE);
    if (!hasActive) {
      // All inactive — archive immediately without showing table or modal
      runArchival(parsedNames);
    } else {
      setFetchStatus('done');
    }
  }, [parsedNames, runArchival]);

  // -------------------------------------------------------------------------
  // Derived state
  // -------------------------------------------------------------------------
  const filteredRows = useMemo(() => {
    if (!searchText.trim()) return variableRows;
    const lower = searchText.toLowerCase();
    return variableRows.filter((r) => r.name.toLowerCase().includes(lower));
  }, [variableRows, searchText]);

  const handleModalConfirm = useCallback(() => {
    setModalOpen(false);
    setFetchStatus('idle');
    handleProceedArchival();
  }, [handleProceedArchival]);

  // -------------------------------------------------------------------------
  // Row selection — preserves selection state across search changes
  // -------------------------------------------------------------------------
  const rowSelection = {
    columnWidth: 40,
    selectedRowKeys: filteredRows
      .filter((r) => selectedKeys.has(r.name))
      .map((r) => r.name),
    onChange: (_keys: React.Key[], selectedRows: VariableRow[]) => {
      const visibleNames = new Set(filteredRows.map((r) => r.name));
      setSelectedKeys((prev) => {
        const next = new Set(prev);
        visibleNames.forEach((n) => next.delete(n));
        selectedRows.forEach((r) => next.add(r.name));
        return next;
      });
    },
  };

  // -------------------------------------------------------------------------
  // Columns
  // -------------------------------------------------------------------------
  const previewColumns: ColumnsType<VariableRow> = [
    {
      title: 'Variable Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Variable State',
      dataIndex: 'state',
      key: 'state',
      width: 120,
      render: (state: string) =>
        state === VariableState.ACTIVE ? (
          <Tag color="success">Active</Tag>
        ) : (
          <Tag>Inactive</Tag>
        ),
    },
    {
      title: 'Last Triggered At',
      dataIndex: 'lastTriggeredAt',
      key: 'lastTriggeredAt',
      width: 180,
      render: (date?: Date | null) =>
        date ? new Date(date).toLocaleString() : '—',
    },
  ];

  const archivalColumns: ColumnsType<ArchivalItem> = [
    {
      title: 'Variable Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: OperationStatus) => STATUS_TAG[status],
    },
    {
      title: 'Error Reason',
      dataIndex: 'error',
      key: 'error',
      render: (errorMsg?: string) => errorMsg ?? '—',
    },
  ];

  const successCount = archivalItems.filter(
    (i) => i.status === 'success',
  ).length;
  const failedItems = archivalItems.filter((i) => i.status === 'error');
  const isArchivalComplete = !isArchiving && archivalItems.length > 0;
  const showPreviewTable = fetchStatus === 'done' && variableRows.length > 0;

  return (
    <Flex vertical gap={16}>
      <Input.TextArea
        placeholder="Enter variable names, one per line or comma-separated"
        rows={4}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          setFetchStatus('idle');
          setVariableRows([]);
          setArchivalItems([]);
        }}
        disabled={isArchiving || fetchStatus === 'loading'}
      />
      <Flex justify="flex-end">
        <Button
          size="small"
          type="primary"
          onClick={handleFetchVariables}
          loading={fetchStatus === 'loading'}
          disabled={parsedNames.length === 0 || isArchiving}
        >
          Load Variables
        </Button>
      </Flex>

      {showPreviewTable && <Divider style={{ margin: 0 }} />}

      {/* Preview table: shown after variable info is loaded */}
      {showPreviewTable && (
        <Flex vertical gap={8}>
          <Flex justify="space-between" align="center">
            <Input
              placeholder="Search variable names"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 280 }}
              allowClear
            />
            <Button
              danger
              type="primary"
              size="small"
              disabled={selectedKeys.size === 0 || isArchiving}
              loading={isArchiving}
              onClick={() => setModalOpen(true)}
            >
              Proceed with Archival
            </Button>
          </Flex>

          <Table
            dataSource={filteredRows}
            columns={previewColumns}
            rowKey="name"
            pagination={false}
            size="small"
            virtual
            scroll={{ y: 320 }}
            rowSelection={rowSelection}
          />
        </Flex>
      )}

      {/* Archival result table */}
      {archivalItems.length > 0 && (
        <Table
          dataSource={archivalItems}
          columns={archivalColumns}
          rowKey="name"
          pagination={false}
          size="small"
        />
      )}

      {isArchivalComplete && (
        <Alert
          type="info"
          message={
            <Flex vertical gap={4}>
              <Typography.Text>
                {successCount} request{successCount !== 1 ? 's' : ''}{' '}
                successful.
              </Typography.Text>
              <Typography.Text>
                {failedItems.length} request
                {failedItems.length !== 1 ? 's' : ''} failed.
              </Typography.Text>
              {failedItems.length > 0 && (
                <Flex vertical gap={2}>
                  <Typography.Text strong>Failed variables:</Typography.Text>
                  {failedItems.map((item) => (
                    <Typography.Text key={item.name}>
                      {item.name}
                      {item.error ? ` — ${item.error}` : ''}
                    </Typography.Text>
                  ))}
                </Flex>
              )}
            </Flex>
          }
        />
      )}

      <Modal
        open={modalOpen}
        title="Proceed with Archival"
        centered
        onOk={handleModalConfirm}
        onCancel={() => {
          setModalOpen(false);
          setFetchStatus('idle');
        }}
        okText="Confirm"
        cancelText="Cancel"
        okButtonProps={{ danger: true, size: 'small' }}
        cancelButtonProps={{ size: 'small' }}
      >
        <Typography.Text>
          Are you sure you want to archive the selected variables?
        </Typography.Text>
      </Modal>
    </Flex>
  );
};

const tabItems = [
  {
    key: 'bulk-archival',
    label: 'Bulk Archival',
    children: <BulkArchivalTab />,
  },
  {
    key: 'bulk-deletion',
    label: 'Bulk Deletion',
    children: <BulkDeletionTab />,
  },
  {
    key: 'variable-complexity-policy',
    label: 'Variable Complexity Policy',
    children: <VariableComplexityPolicyTab />,
  },
  {
    key: 'variable-complexity',
    label: 'Variable Complexity',
    children: <VariableComplexityTab />,
  },
  {
    key: 'service-onboarding',
    label: 'Service Onboarding',
    children: <ServiceOnboardingTab />,
  },
];

const AdminPage: React.FC = () => {
  return (
    <ObjectDetailPage>
      <Flex
        vertical
        gap={16}
        style={{
          padding: 24,
          height: '100%',
          overflow: 'hidden auto',
        }}
      >
        <ConfigProvider
          theme={{
            components: {
              Typography: {
                titleMarginBottom: 0,
              },
            },
          }}
        >
          <Typography.Title level={3}>Admin</Typography.Title>
        </ConfigProvider>
        <Card>
          <Tabs
            defaultActiveKey="bulk-archival"
            size="small"
            items={tabItems}
          />
        </Card>
      </Flex>
    </ObjectDetailPage>
  );
};

export default AdminPage;
